import { CheckoutWithCard } from '@paperxyz/react-client-sdk'
import PropTypes from 'prop-types'
import { memo, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { getContract, prepareContractCall } from 'thirdweb'
import { polygon, polygonAmoy } from 'thirdweb/chains'
import { TransactionButton } from 'thirdweb/react'
import { parseEther } from 'viem'
import constants from 'app/constants'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import loadAbi from 'app/services/UserProvider/hooks/actions/loadAbi'
import tagManager from 'app/tagManager'
import { ROOT, thirdwebClient as client } from 'app/thread'
import Alert from 'components/core/Form/Alert/Alert'
import Form from 'components/core/Form/Form'
import Layout from 'components/core/Layout/Layout'
import ButtonActivity from 'components/elements/ButtonActivity/ButtonActivity'
import NotifySymbol from 'components/elements/NotifySymbol/NotifySymbol'
import SimpleModal from 'components/elements/SimpleModal/SimpleModal'
import TransitionSwap from 'components/elements/TransitionSwap/TransitionSwap'
import UtilityTermsDialog from 'components/interface/UtilityTermsDialog/UtilityTermsDialog'
import styles from './MintAccessories.module.scss'
import useMintAccessories from './hooks/useMintAccessories'

/**
 * The `MintAccessories`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function MintAccessories (props) {
  const [isTermsOpen, setIsTermsOpen] = useState(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const {
    doesRequireUtilityTerms,
    errorMessage,
    isBusyStatus,
    isOutOfStock,
    isPaperCheckoutOpen,
    isTransactionComplete,
    isVerifyingSupply,
    onCheckoutWithPaper,
    onCheckoutWithWallet,
    onPaperCloseModal,
    onPaperSuccess,
    oncheckoutWithThirdwebPay,
    paperCheckoutConfig,
    setErrorMessage
  } = useMintAccessories(props)
  // eslint-disable-next-line react/prop-types
  const { checkoutData } = props
  const { account } = useUserContext()

  /**
   * Memo: The verifying supply status label
   */
  const statusVerifySupply = useMemo(() => {
    if (isVerifyingSupply) {
      return (
        <div className={styles.verify}>
          <span className="text-gradient">Verifying supply...</span>
        </div>
      )
    }

    return null
  }, [isVerifyingSupply])

  /**
   * Handle click checkout with paper
   */
  const handleClickCheckoutWithPaper = useCallback(() => {
    if (doesRequireUtilityTerms && !isTermsAccepted) {
      setErrorMessage('Please accept the terms.')

      return
    }

    tagManager.trackEvent({
      event: 'marketplace.purchase-button-click',
      action: {
        type: 'inApp'
      }
    })

    onCheckoutWithPaper()
  }, [onCheckoutWithPaper, doesRequireUtilityTerms, isTermsAccepted])

  /**
   * Handle click checkout with wallet
   */
  const handleClickCheckoutWithWallet = useCallback(() => {
    if (doesRequireUtilityTerms && !isTermsAccepted) {
      setErrorMessage('Please accept the terms.')

      return
    }

    tagManager.trackEvent({
      event: 'marketplace.purchase-button-click',
      action: { type: 'wallet' }
    })

    onCheckoutWithWallet()
  }, [onCheckoutWithWallet, doesRequireUtilityTerms, isTermsAccepted])

  /**
   * Handle click terms
   */
  const handleClickTerms = useCallback(e => {
    setIsTermsOpen(true)
  }, [])

  /**
   * Handle close the terms
   */
  const handleCloseTerms = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    setIsTermsOpen(false)
  }, [])

  return (
    <div className={styles.container}>
      <Layout.Body align="center">
        {isOutOfStock && (
          <TransitionSwap animation="accordion" delayHide={0.2}>
            <div className={styles.outOfStock}>
              <NotifySymbol
                className={styles.notify}
                icon="close"
                color="warn"
              />
              <h2 className="text-gradient">Oops!</h2>
              <p>
                Some of the products in your cart are now{' '}
                <span className="text-gradient">out of stock.</span> and have
                been removed from your cart. Please review your cart before
                proceeding to checkout.
              </p>
            </div>
          </TransitionSwap>
        )}

        {isTransactionComplete && (
          <TransitionSwap animation="accordion" delayHide={0.2}>
            <div className={styles.complete}>
              <NotifySymbol className={styles.notify} icon="check" />
              <h2 className="text-gradient">Transaction Complete</h2>
            </div>
          </TransitionSwap>
        )}

        {!isTransactionComplete && !isOutOfStock && (
          <>
            <p>
              Please <span className="text-gradient">select an option</span> to
              complete your purchase.
            </p>
            <div className={styles.form}>
              {doesRequireUtilityTerms && (
                <Form className={styles.termsBlock}>
                  <Form.Checkbox
                    value={isTermsAccepted}
                    onChange={e => {
                      setIsTermsAccepted(e.target.checked)
                    }}
                    label={
                      <span>
                        Please check the box to indicate that you have read and
                        agree to our{' '}
                        <button
                          className={styles.button}
                          onClick={handleClickTerms}
                        >
                          Utility Terms & Conditions
                        </button>{' '}
                        before proceeding.
                      </span>
                    }
                  />
                </Form>
              )}
              <TransitionSwap animation="accordion" delayHide={0.2}>
                {statusVerifySupply}
              </TransitionSwap>
              <div className={styles.options}>
                {
                constants.IS_CREDIT_CARD_ENABLED
                  ? (
                    <ButtonActivity
                      size="large"
                      color="a"
                      isBusy={isBusyStatus}
                      onClick={handleClickCheckoutWithPaper}
                      icon="creditcard"
                      side="right"
                      useHighlight={true}
                      borderRadius="c"
                    >
                      Pay with Card
                    </ButtonActivity>
                    )
                  : account?.wallet?.provider === 'inApp'
                    ? (
                      <TransactionButton
                        // eslint-disable-next-line react/no-children-prop
                        children={'Make Payment'}

                        transaction={async () => {
                          const contractInfo = constants.CONTRACTS.AccessoryCollection

                          const response = await loadAbi(contractInfo.abiUrl)

                          console.log(response.abi, contractInfo.address)

                          // Create a transaction object and return it
                          const tx = prepareContractCall({
                            contract: getContract({
                              address: contractInfo.address,
                              abi: response?.abi,
                              chain: constants.CHAIN === 137 ? polygon : polygonAmoy,
                              client

                            }),
                            method: 'batchPaidMint',
                            // eslint-disable-next-line react/prop-types
                            params: [checkoutData.itemIds, checkoutData.itemQuantities, account?.wallet?.address],
                            // eslint-disable-next-line react/prop-types
                            value: parseEther(checkoutData.total.toString())

                          })

                          return tx
                        }}
                        onTransactionConfirmed={oncheckoutWithThirdwebPay}
                        onTransactionSent={e => console.log(e)}

                        onError={e => {
                          setErrorMessage('Transaction failed. Please try again.')
                          console.log(e)
                        }}
                      // payModal={
                      //   {
                      //     buyWithCrypto: false
                      //   }
                      // }

                      />
                      )
                    : (
                        ''
                      )}
                {account?.wallet?.provider === 'wagmi' && (
                  <ButtonActivity
                    size="large"
                    color="c"
                    onClick={handleClickCheckoutWithWallet}
                    side="right"
                    icon="wallet"
                    isBusy={isBusyStatus}
                    borderRadius="c"
                  >
                    Pay with Wallet
                  </ButtonActivity>
                )}
              </div>
              <Alert isActive={!!errorMessage}>{errorMessage}</Alert>
            </div>
          </>
        )}
      </Layout.Body>
      {isPaperCheckoutOpen &&
        createPortal(
          <SimpleModal onClose={onPaperCloseModal}>
            <CheckoutWithCard
              configs={paperCheckoutConfig}
              onPaymentSuccess={onPaperSuccess}
            />
          </SimpleModal>,
          ROOT
        )}
      {isTermsOpen &&
        createPortal(
          <SimpleModal size="large" color="b" onClose={handleCloseTerms}>
            <UtilityTermsDialog />
          </SimpleModal>,
          ROOT
        )}
    </div>
  )
}

MintAccessories.propTypes = {
  checkoutData: PropTypes.shape({
    itemIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    itemQuantities: PropTypes.arrayOf(PropTypes.number).isRequired,
    total: PropTypes.number.isRequired
  }).isRequired
}

export default memo(MintAccessories)